import React, {
  useContext,
  useMemo,
} from 'react';
import {
  VisitInfo,
} from 'pages/Dashboard/pages/Billing/types';
import MoveDownIcon from '@mui/icons-material/MoveDownRounded';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  useFormContext,
} from 'react-hook-form';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import {
  useShiftToPatient,
} from 'pages/Dashboard/pages/Billing/hooks';
import Alert from 'pages/Dashboard/components/Alert';
import {
  EOBServiceCodePaymentInfoDTO,
} from 'dtos/eOBServiceCodePaymentInfoDTO';
import Spinner from 'pages/Dashboard/components/Spinner';
import sumBy from 'lodash/sumBy';
import IconButton from '@mui/material/IconButton';
import {
  isNilOrZero,
} from '@populateui/utils';
import Tooltip from 'core/components/Tooltip';
import clsx from 'clsx';

export default function EOBActions(data: VisitInfo) {
  const { isShiftedToPatient, claimId } = data;

  const { confirm, closeConfirm } = useConfirmDialog();
  const { eobId, fetchBillingData } = useContext(WizardContext);
  const notify = useNotify();
  const {
    mutateAsync: shiftToPatient,
    isPending: isLoading,
  } = useShiftToPatient();
  const { watch, setValue } = useFormContext();
  const eobServiceCodePaymentsInfo: EOBServiceCodePaymentInfoDTO[] = watch('eob.eobServiceCodePaymentInfos');

  const isNonCoveredEmpty = useMemo(() => (
    isNilOrZero(
      sumBy(
        eobServiceCodePaymentsInfo.filter((info) => info?.claim?.claimId === claimId),
        'nonCovered',
      ),
    )
  ), [eobServiceCodePaymentsInfo, claimId]);

  const onShiftToPatient = () => {
    confirm({
      title: 'Shift to patient\'s responsibility',
      className: '!space-y-0 !p-0 !p-4 !gap-1',
      classes: { footer: 'pt-3' },
      content: (
        <div className="flex flex-col gap-4 -mx-4 !mb-3">
          <Alert
            open
            severity="error"
            classes={{
              root: '!p-0 !relative gap-2',
              icon: '!py-0 !absolute !top-1 !left-3',
              message: '!py-0 gap-2 overflow-hidden w-full',
            }}
          >
            <div className="font-semibold ml-8 mt-1.5 px-2">
              <span className="uppercase">Warning</span>
              : This action cannot be undone
            </div>
            <div className="w-full border-y border-y-red-600 my-2" />
            <ul className="list-disc list-inside gap-2 flex flex-col px-4 pb-4">
              <li>
                <strong className="font-semibold">Cash/Non-covered</strong>
                {' '}
                amount will be shifted to patient.
              </li>
              <li>
                <strong className="font-semibold">Copay, Coinsurance, and Deductible</strong>
                {' '}
                will be set to
                {' '}
                <strong className="font-semibold">$0.00</strong>
                .
              </li>
            </ul>
          </Alert>
        </div>
      ),
      onConfirm: async () => {
        closeConfirm();
        await shiftToPatient({ eobId: eobId ?? 0, claimId: claimId ?? 0 });
        const updatedEobServiceCodePaymentsInfo = eobServiceCodePaymentsInfo
          .map((info: EOBServiceCodePaymentInfoDTO) => (
            info.claimId === claimId ? {
              ...info,
              deductible: 0,
              copay: 0,
              coinsurance: 0,
            } : info
          ));
        setValue('eob.eobServiceCodePaymentInfos', updatedEobServiceCodePaymentsInfo);
        await fetchBillingData?.();
        notify({
          variant: 'success',
          message: 'Shifted to patient\'s responsibility successfully',
        });
      },
    });
  };

  const isDisabled = useMemo(() => (
    isNonCoveredEmpty || isShiftedToPatient
  ), [isNonCoveredEmpty, isShiftedToPatient]);

  const tooltipTitle = useMemo(() => (
    isNonCoveredEmpty
      ? (
        <div className="p-1">
          No
          {' '}
          <strong className="font-semibold">Cash/Non-covered</strong>
          {' '}
          amounts are available for shifting to patient responsibility.
        </div>
      )
      : 'Shift to patient'
  ), [isNonCoveredEmpty]);

  return (
    <Tooltip title={isShiftedToPatient ? 'Shifted to Patient' : tooltipTitle}>
      <span>
        <IconButton
          size="small"
          disableRipple
          className={clsx('!p-0', {
            '!text-gray-500': isShiftedToPatient,
            '!text-blue-300': !isShiftedToPatient && isNonCoveredEmpty,
            '!text-blue-500': !isShiftedToPatient && !isNonCoveredEmpty,
            '!cursor-not-allowed !pointer-events-auto': isDisabled,
          })}
          disabled={isDisabled ?? false}
          color="info"
          onClick={(e) => {
            e.stopPropagation();
            onShiftToPatient();
          }}
        >
          {isLoading ? <Spinner /> : <MoveDownIcon className="!w-4 !h-4" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
